import React, { useState } from 'react';
import { SEO } from '../components/SEO';
import { buildBreadcrumbList, buildServiceSchema } from '../lib/structuredData';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ArrowRightIcon, MailIcon, ShieldIcon, CheckCircleIcon, SparklesIcon, UsersIcon, SendIcon, LockIcon, BotIcon, StarIcon, CrownIcon, BriefcaseIcon, FileTextIcon, ReceiptIcon, TrendingUpIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type PackageId = 'starter' | 'standard' | 'premium' | 'google';
type EmailPackage = {
  id: PackageId;
  name: string;
  shortName: string;
  tagline: string;
  price: string;
  unit: string;
  storage: string;
  description: string;
  features: string[];
  hasKodee: boolean;
  badge?: string;
  icon: React.ReactNode;
};
const emailPackages: EmailPackage[] = [{
  id: 'starter',
  name: 'Starter Business Email',
  shortName: 'Starter',
  tagline: 'Voor zzp en freelancers die professioneel willen mailen',
  price: '€3,99',
  unit: 'per mailbox per maand',
  storage: '5 GB',
  description: 'Een professioneel mailadres op eigen domein, met Kodee AI inbegrepen. Geschikt als startpunt voor zelfstandige adviseurs en kleine kantoren.',
  features: ['5 GB opslag per mailbox', '5 doorstuurregels en 5 aliassen', 'Werkt met Outlook, Apple Mail, Gmail-app en elke andere mailapp', 'Geavanceerde bescherming tegen spam, malware en phishing', 'SPF, DKIM, DMARC en ARC standaard ingesteld bij oplevering', 'Versleutelde opslag en geen Hostinger-handtekening', 'Gratis domein bij eerste jaar'],
  hasKodee: true,
  icon: <MailIcon className="h-5 w-5" data-id="element-1519" data-mp="3op9vsext34og" />
}, {
  id: 'standard',
  name: 'Standard Business Email',
  shortName: 'Standard',
  tagline: 'Voor kleine teams die meer ruimte en meer aliassen willen',
  price: '€4,99',
  unit: 'per mailbox per maand',
  storage: '20 GB',
  description: 'De middelste optie. Ruim genoeg voor adviseurs die regelmatig bijlagen versturen en meerdere aliassen willen voor verschillende rollen of diensten.',
  features: ['20 GB opslag per mailbox', '20 doorstuurregels en 10 aliassen', 'Werkt met Outlook, Apple Mail, Gmail-app en elke andere mailapp', 'Geavanceerde bescherming tegen spam, malware en phishing', 'SPF, DKIM, DMARC en ARC standaard ingesteld bij oplevering', 'Versleutelde opslag en geen Hostinger-handtekening', 'Gratis domein bij eerste jaar'],
  hasKodee: true,
  badge: 'Aanrader',
  icon: <SparklesIcon className="h-5 w-5" data-id="element-1520" data-mp="1r1fxmf0l1q6f" />
}, {
  id: 'premium',
  name: 'Premium Business Email',
  shortName: 'Premium',
  tagline: 'Voor groeiende teams die maximale ruimte en aliassen nodig hebben',
  price: '€6,99',
  unit: 'per mailbox per maand',
  storage: '50 GB',
  description: 'Voor kantoren waar veel mail binnenkomt en uitgaat. Maximale opslag, maximale aliassen en alle AI-tools.',
  features: ['50 GB opslag per mailbox', '50 doorstuurregels en 30 aliassen', 'Werkt met Outlook, Apple Mail, Gmail-app en elke andere mailapp', 'Geavanceerde bescherming tegen spam, malware en phishing', 'SPF, DKIM, DMARC en ARC standaard ingesteld bij oplevering', 'Versleutelde opslag en geen Hostinger-handtekening', 'Gratis domein bij eerste jaar'],
  hasKodee: true,
  icon: <CrownIcon className="h-5 w-5" data-id="element-1521" data-mp="12ixgwgho2vbx" />
}, {
  id: 'google',
  name: 'Google Workspace Starter',
  shortName: 'Google Workspace',
  tagline: 'Voor teams die volledig in het Google-ecosysteem werken',
  price: '€10,99',
  unit: 'per gebruiker per maand',
  storage: '30 GB',
  description: 'E-mail op eigen domein, maar dan in de bekende Google-omgeving. Inclusief Gmail, Drive, Agenda, Meet, Docs, Sheets en Slides.',
  features: ['30 GB opslag per gebruiker', 'Gmail op eigen domein met geavanceerde phishing- en malware-bescherming', 'Google Agenda, Drive, Meet, Docs, Sheets en Slides', 'Chat-teamberichten en AppSheet', 'Videoconferenties tot 100 deelnemers', 'Centraal beheer via admin console', 'Wij regelen de DNS, domeinverificatie en oplevering'],
  hasKodee: false,
  icon: <UsersIcon className="h-5 w-5" data-id="element-1522" data-mp="3ko6xim1ohxck" />
}];
type ReachTier = {
  id: string;
  range: string;
  contacts: string;
  emails: string;
  aiCredits: string;
  price: string;
};
const reachTiers: ReachTier[] = [{
  id: 'tier-1',
  range: '0 tot 500 contacten',
  contacts: '500 contacten per maand',
  emails: '3.500 e-mails per maand',
  aiCredits: '10 AI-credits per maand',
  price: '€6,99'
}, {
  id: 'tier-2',
  range: '501 tot 1.000 contacten',
  contacts: '1.000 contacten per maand',
  emails: '7.000 e-mails per maand',
  aiCredits: '20 AI-credits per maand',
  price: '€12,99'
}, {
  id: 'tier-3',
  range: '1.001 tot 2.500 contacten',
  contacts: '2.500 contacten per maand',
  emails: '17.500 e-mails per maand',
  aiCredits: '30 AI-credits per maand',
  price: '€27,99'
}, {
  id: 'tier-4',
  range: '2.501 tot 5.000 contacten',
  contacts: '5.000 contacten per maand',
  emails: '35.000 e-mails per maand',
  aiCredits: '30 AI-credits per maand',
  price: '€50,99'
}, {
  id: 'tier-5',
  range: '5.001 tot 10.000 contacten',
  contacts: '10.000 contacten per maand',
  emails: '70.000 e-mails per maand',
  aiCredits: '30 AI-credits per maand',
  price: '€80,99'
}, {
  id: 'tier-6',
  range: '10.001 tot 50.000 contacten',
  contacts: '50.000 contacten per maand',
  emails: '350.000 e-mails per maand',
  aiCredits: '30 AI-credits per maand',
  price: '€270,99'
}];
export const EmailPage: React.FC = () => {
  const [selectedPackage, setSelectedPackage] = useState<PackageId>('standard');
  const [selectedReach, setSelectedReach] = useState<string>('tier-1');
  const activePackage = emailPackages.find(p => p.id === selectedPackage) ?? emailPackages[1];
  const activeReach = reachTiers.find(t => t.id === selectedReach) ?? reachTiers[0];
  return <div className="w-full min-h-screen bg-white text-[#111827] font-sans" data-id="element-1523" data-mp="8e3y7giczv8u">
      <SEO title="Zakelijke e-mail hosting met pakket-keuze en Kodee AI | Noord Digital" description="Vergelijk Starter, Standard, Premium Business Email en Google Workspace. Inclusief Kodee AI, agentic mail en email marketing via Hostinger Reach. Vaste maandprijs, jaarlijks vastgelegd." keywords="zakelijke e-mail hosting, business email starter, business email standard, business email premium, kodee ai, google workspace, eigen domein e-mail, email marketing, hostinger reach" canonical="https://noorddigital.nl/email" jsonLd={[buildServiceSchema({
      name: 'E-mail',
      description: 'Zakelijke e-mail op je eigen domein, netjes ingericht en beveiligd tegen spam en phishing, met keuze uit meerdere pakketten.',
      path: '/email',
      serviceType: 'Zakelijke e-mailhosting'
    }), buildBreadcrumbList([{
      name: 'E-mail',
      path: '/email'
    }])]} data-id="element-1524" data-mp="26r3wj6gvfmsz" />
      <Header data-id="element-1525" data-mp="erhwajzfm53y" />

      {/* Hero */}
      <div className="w-full bg-gradient-to-br from-[#007AFF] to-[#1B2B44] py-16 md:py-20" data-id="element-1526" data-mp="eqg9xjcytw6x">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1527" data-mp="2uhufbmrad9hx">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center" data-id="element-1528" data-mp="2zjjsp91zdz96">
            <div className="lg:col-span-7" data-id="element-1529" data-mp="2r0q6zdbk59pj">
              <h1 className="font-bold text-white leading-tight mb-6 text-4xl md:text-5xl lg:text-6xl" data-id="element-1530" data-mp="2qzol0dt072t1">
                Zakelijke e-mail met de pakket-keuze die bij je werk past
              </h1>
              <p className="text-white text-opacity-90 mb-8 text-lg leading-relaxed" data-id="element-1531" data-mp="30yw65n5zc1qp">
                Vier pakketten, allemaal met e-mail op je eigen domein. Bij elk Business-pakket krijg je Kodee AI inbegrepen, de assistent die helpt met opstellen, samenvatten en doorzoeken. Of kies Google Workspace als je liever in de Google-omgeving werkt. Wij regelen de DNS, beveiliging en oplevering, jij krijgt een werkende mailbox op de afgesproken dag. Voor ondernemers in{' '}
                <a href="/werkgebied/alkmaar" className="underline underline-offset-4 decoration-white/50 hover:decoration-white transition-colors duration-200" data-id="element-1532" data-mp="2d91sx4hr0b5x">Alkmaar</a>{' '}
                en de rest van Noord-Holland Noord.
              </p>
              <div className="flex flex-wrap gap-4" data-id="element-1533" data-mp="1kjl4ehx91eez">
                <a href="#email-pakketten" className="inline-flex items-center px-6 py-3 bg-white text-[#007AFF] font-semibold rounded-lg shadow-lg" data-id="element-1534" data-mp="z3e96u89y72i">
                  Vergelijk de pakketten
                  <ArrowRightIcon className="h-5 w-5 ml-2" data-id="element-1535" data-mp="18z8uxy7soi9i" />
                </a>
                <a href="#mailbox-advies" className="inline-flex items-center px-6 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-lg" data-id="element-1536" data-mp="1dzwmqm3ul34s">
                  Ons mailbox-advies
                  <ArrowRightIcon className="h-5 w-5 ml-2" data-id="element-1537" data-mp="1mzjlt193twuo" />
                </a>
              </div>
            </div>

            <div className="lg:col-span-5" data-id="element-1538" data-mp="1rrtc4ie4syxh">
              <div className="bg-white rounded-2xl p-8 shadow-2xl" data-id="element-1539" data-mp="1rt4d6ag6zskw">
                <div className="text-center mb-6" data-id="element-1540" data-mp="1wpblg9w29g60">
                  <div className="inline-flex items-center justify-center w-32 h-32 bg-gradient-to-br from-[#007AFF] to-[#005FCC] rounded-full mb-4 p-3" data-id="element-1541" data-mp="21p7s6gw57lp9">
                    <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden mascotte, zakelijke e-mail hosting" className="w-full h-full object-cover rounded-full" data-id="element-1542" data-mp="25qsytki333r0" />
                  </div>
                  <h3 className="font-bold text-[#111827] text-2xl mb-2" data-id="element-1543" data-mp="3h7nfrsmx3xzk">
                    Norden bewaakt jouw mailverkeer
                  </h3>
                  <p className="text-[#6B7280]" data-id="element-1544" data-mp="3cadkvxgreu4c">
                    Aflevergarantie, anti-phishing en geen rare provider-branding onderaan je mail
                  </p>
                </div>
                <div className="space-y-3 pt-6 border-t border-gray-200" data-id="element-1545" data-mp="8tez9ixo6xnr">
                  <div className="flex items-center gap-3" data-id="element-1546" data-mp="40lpuhr9b15c">
                    <ShieldIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-1547" data-mp="3z0x8xoi5sa4" />
                    <span className="text-sm text-[#6B7280]" data-id="element-1548" data-mp="3v5kh12cbgskk">
                      Geavanceerde beveiliging (SPF, DKIM, DMARC, ARC)
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-1549" data-mp="rpdex09jv3dw">
                    <BotIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-1550" data-mp="3j3fl0ih8lzsf" />
                    <span className="text-sm text-[#6B7280]" data-id="element-1551" data-mp="3e4vzgj6k2q0l">
                      Kodee AI inbegrepen bij alle Business-pakketten
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-1552" data-mp="3szscd9pb81k0">
                    <LockIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-1553" data-mp="3o2902fc69yup" />
                    <span className="text-sm text-[#6B7280]" data-id="element-1554" data-mp="5wnjiqqz321s">
                      Spamfilter, virusbescherming en aflevergarantie
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pakket-keuze met toggle */}
      <div id="email-pakketten" className="w-full bg-white py-20 md:py-24" data-id="element-1555" data-mp="apgqll9wymh1">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1556" data-mp="on5b2s02jrlk">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1557" data-mp="kmldxofhwnzf">
            <h2 className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-1558" data-mp="ko66j8i91wun">
              Kies het pakket dat bij je werk past
            </h2>
            <p className="text-[#6B7280] text-lg leading-relaxed" data-id="element-1559" data-mp="18a8hg8k6r8oe">
              Vier pakketten met e-mail op je eigen domein. Bij elk Business-pakket zit Kodee AI inbegrepen. Tik op een pakket om de prijs, opslag en features te zien.
            </p>
          </div>

          {/* Toggle */}
          <div className="max-w-4xl mx-auto mb-10" data-id="element-1560" data-mp="kjma6ji09sfg">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-2 bg-[#F7F8FA] rounded-2xl" data-id="element-1561" data-mp="kitdkbcrgjb1">
              {emailPackages.map(pkg => {
              const isActive = pkg.id === selectedPackage;
              return <button key={pkg.id} onClick={() => setSelectedPackage(pkg.id)} className={`flex items-center justify-center gap-2 px-3 py-3 rounded-xl font-semibold text-sm transition-all duration-200 ${isActive ? 'bg-white text-[#007AFF] shadow-md' : 'bg-transparent text-[#6B7280] hover:text-[#111827]'}`} data-id="element-1562" data-mp="bhd0twyh453n">
                    {pkg.icon}
                    <span data-id="element-1563" data-mp="28a2b024zz8v1">{pkg.shortName}</span>
                    {pkg.badge && isActive && <span className="ml-1 hidden lg:inline-flex items-center gap-1 bg-[#007AFF] text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" data-id="element-1564" data-mp="23itz8zg73bep">
                        <StarIcon className="h-3 w-3" data-id="element-1565" data-mp="1zf4dsiuth9xw" />
                        {pkg.badge}
                      </span>}
                  </button>;
            })}
            </div>
          </div>

          {/* Pakket card (dynamic) */}
          <AnimatePresence mode="wait" data-id="element-1566" data-mp="2n72rtalnhjeq">
            <motion.div key={activePackage.id} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -12
          }} transition={{
            duration: 0.25
          }} className="bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] rounded-2xl p-8 md:p-12 max-w-5xl mx-auto shadow-sm border border-gray-100" data-id="element-1567" data-mp="2n84ao18wra70">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start" data-id="element-1568" data-mp="11vt6qw6g849g">
                {/* Prijs blok */}
                <div className="md:col-span-5" data-id="element-1569" data-mp="woczakdak1zx">
                  <div className="bg-white rounded-2xl p-6 md:p-8 shadow-md" data-id="element-1570" data-mp="wp4iswtm699u">
                    <div className="inline-flex items-center gap-2 bg-[#007AFF] bg-opacity-10 text-[#007AFF] text-xs font-semibold px-3 py-1 rounded-full mb-4" data-id="element-1571" data-mp="11mo94v3l6ci5">
                      {activePackage.icon}
                      <span data-id="element-1572" data-mp="1aezo9kt92dit">{activePackage.name}</span>
                    </div>
                    <p className="text-[#6B7280] text-sm mb-4 leading-relaxed" data-id="element-1573" data-mp="1feku8f6u7m51">
                      {activePackage.tagline}
                    </p>
                    <div className="flex items-baseline gap-2 mb-2" data-id="element-1574" data-mp="1keyxljxhu7wq">
                      <span className="text-5xl font-bold text-[#111827]" data-id="element-1575" data-mp="1pbz1bo6veyko">
                        {activePackage.price}
                      </span>
                    </div>
                    <p className="text-[#6B7280] text-sm mb-4" data-id="element-1576" data-mp="3r453mnlwqjm7">
                      {activePackage.unit}
                    </p>
                    <div className="border-t border-gray-200 pt-4 mb-6" data-id="element-1577" data-mp="1lf1rpcirie6i">
                      <p className="text-xs uppercase tracking-wide text-[#6B7280] mb-1" data-id="element-1578" data-mp="1gi2weqnd416q">
                        Opslag
                      </p>
                      <p className="text-[#111827] font-semibold" data-id="element-1579" data-mp="1ueyipksmjp4q">
                        {activePackage.storage} per mailbox
                      </p>
                    </div>
                    <a href="/#contact" className="inline-flex items-center justify-center w-full bg-[#007AFF] text-white font-semibold py-3 px-6 rounded-lg shadow-md" data-id="element-1580" data-mp="11swz58834l1h">
                      Vraag {activePackage.shortName} aan
                      <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-1581" data-mp="1bk9nvqyl6v3a" />
                    </a>
                    <p className="text-xs text-[#6B7280] text-center mt-3" data-id="element-1582" data-mp="16q5g55hhyj15">
                      Jaarlijks vastgelegd, prijzen excl. BTW. Setup en oplevering door Noord Digital.
                    </p>
                  </div>
                </div>

                {/* Inhoud blok */}
                <div className="md:col-span-7" data-id="element-1583" data-mp="2polccidh4idt">
                  <h3 className="font-bold text-[#111827] text-2xl mb-3" data-id="element-1584" data-mp="2ugmkbs3lzmy1">
                    Wat zit erin?
                  </h3>
                  <p className="text-[#6B7280] leading-relaxed mb-6" data-id="element-1585" data-mp="2gqj1zc7uoddv">
                    {activePackage.description}
                  </p>
                  <ul className="space-y-3 mb-6" data-id="element-1586" data-mp="2lmgm7tm8i3vx">
                    {activePackage.features.map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-1587" data-mp="39c1386ytur2k" data-mpi={i}>
                        <CheckCircleIcon className="h-5 w-5 text-[#007AFF] mt-0.5 flex-shrink-0" data-id="element-1588" data-mp="3defoexpyy2x0" />
                        <span className="text-[#6B7280] leading-relaxed" data-id="element-1589" data-mp="2zf687y1rj8t1">
                          {feature}
                        </span>
                      </li>)}
                  </ul>

                  {/* Kodee AI uitleg bij alle Business tiers */}
                  {activePackage.hasKodee && <div className="bg-gradient-to-br from-[#007AFF] to-[#005FCC] rounded-2xl p-6 text-white shadow-lg" data-id="element-1590" data-mp="1t05hq7vibitu">
                      <div className="flex items-start gap-4" data-id="element-1591" data-mp="18x9dhjadx6k8">
                        <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1592" data-mp="14vnvjjizu236">
                          <BotIcon className="h-6 w-6 text-white" data-id="element-1593" data-mp="103wblruceum0" />
                        </div>
                        <div data-id="element-1594" data-mp="v5uuvdke37dp">
                          <h4 className="font-bold text-lg mb-2" data-id="element-1595" data-mp="v4jpxaf7jtl0">
                            Kodee AI inbegrepen
                          </h4>
                          <p className="text-white text-opacity-90 leading-relaxed text-sm mb-3" data-id="element-1596" data-mp="l7x3wib5awhe">
                            Bij alle Business-pakketten krijg je Kodee, de AI assistent die naast je werkt in je mailbox. Geen extra abonnement, geen losse koppeling.
                          </p>
                          <ul className="space-y-2 text-sm text-white text-opacity-90" data-id="element-1597" data-mp="2bkw5grwc9hi5">
                            <li className="flex items-start gap-2" data-id="element-1598" data-mp="26l00oqfthm1j">
                              <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" data-id="element-1599" data-mp="26jowipigmc9w" />
                              <span data-id="element-1600" data-mp="2e2y4cebbi2wu">Slimme AI-antwoorden en berichtconcepten in jouw eigen toon</span>
                            </li>
                            <li className="flex items-start gap-2" data-id="element-1601" data-mp="2nubz68sbqejn">
                              <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" data-id="element-1602" data-mp="2nx7wpw09f17k" />
                              <span data-id="element-1603" data-mp="2sw1bdrra8y9b">Directe AI-samenvattingen van lange threads</span>
                            </li>
                            <li className="flex items-start gap-2" data-id="element-1604" data-mp="2059jh6h92vrs">
                              <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" data-id="element-1605" data-mp="205jbt0prpf0n" />
                              <span data-id="element-1606" data-mp="252sxq6dy78jq">AI-gestuurde e-mailzoekfunctie op onderwerp in plaats van trefwoord</span>
                            </li>
                            <li className="flex items-start gap-2" data-id="element-1607" data-mp="3gjj7fzhkyii3">
                              <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" data-id="element-1608" data-mp="3gj9i8e4d0fdg" />
                              <span data-id="element-1609" data-mp="3kjjmnmci6xq7">Agentische e-mail (bèta): laat Kodee zelfstandig acties oppakken</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Vergelijkings-strook */}
          <div className="max-w-5xl mx-auto mt-10 grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-1610" data-mp="ffbzjh0fncij">
            {emailPackages.map(pkg => {
            const isActive = pkg.id === selectedPackage;
            return <button key={pkg.id} onClick={() => setSelectedPackage(pkg.id)} className={`text-left p-5 rounded-xl border-2 transition-all duration-200 ${isActive ? 'border-[#007AFF] bg-white shadow-md' : 'border-transparent bg-[#F7F8FA] hover:border-gray-200'}`} data-id="element-1611" data-mp="o7nfg8xx7hk9">
                  <div className="flex items-center gap-2 mb-2" data-id="element-1612" data-mp="34x18h5uiywzw">
                    <div className="w-8 h-8 bg-[#007AFF] bg-opacity-10 rounded-full flex items-center justify-center text-[#007AFF]" data-id="element-1613" data-mp="34xkpmkccphcv">
                      {pkg.icon}
                    </div>
                    <span className="font-semibold text-[#111827] text-sm leading-tight" data-id="element-1614" data-mp="1fgyyyno19sw4">
                      {pkg.shortName}
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-[#111827]" data-id="element-1615" data-mp="15pkt7y5hnwuj">
                    {pkg.price}
                  </p>
                  <p className="text-xs text-[#6B7280] mb-2" data-id="element-1616" data-mp="1anme8omh668n">{pkg.unit}</p>
                  <p className="text-xs text-[#007AFF] font-semibold" data-id="element-1617" data-mp="wowet2c6nuh4">
                    {pkg.storage} opslag
                  </p>
                </button>;
          })}
          </div>
        </div>
      </div>

      {/* 3-mailbox advies */}
      <div id="mailbox-advies" className="w-full bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] py-20 md:py-24" data-id="element-1618" data-mp="mtupo6hwqsej">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1619" data-mp="2h5j6ke7p951k">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1620" data-mp="2h3gbvgvlqnqi">
            <span className="inline-block bg-[#007AFF] bg-opacity-10 text-[#007AFF] text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full mb-4" data-id="element-1621" data-mp="2m3b17fzqb59g">
              Ons advies
            </span>
            <h2 className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-1622" data-mp="3ugacghpczhm9">
              Drie mailboxen, drie functies, één strak systeem
            </h2>
            <p className="text-[#6B7280] text-lg leading-relaxed" data-id="element-1623" data-mp="3kpfmp3cwlzw2">
              We adviseren elke adviespraktijk om de mail te splitsen over drie functies. Het houdt je inbox overzichtelijk en zorgt dat de juiste mensen de juiste mail krijgen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto" data-id="element-1624" data-mp="h6mel62wa39a">
            {/* Info */}
            <div className="bg-white rounded-2xl p-8 shadow-md" data-id="element-1625" data-mp="88aemdg51t46">
              <div className="w-14 h-14 bg-[#007AFF] bg-opacity-10 rounded-full flex items-center justify-center text-[#007AFF] mb-4" data-id="element-1626" data-mp="86h8r0dcncx8">
                <BriefcaseIcon className="h-7 w-7" data-id="element-1627" data-mp="2sy9lejlg5w57" />
              </div>
              <p className="text-xs uppercase tracking-wide text-[#6B7280] mb-1" data-id="element-1628" data-mp="2swgdz230f7w5">
                Algemene communicatie
              </p>
              <h3 className="font-bold text-[#111827] text-xl mb-3 font-mono" data-id="element-1629" data-mp="2gggnci6yq7k3">
                info@<span className="text-[#007AFF]" data-id="element-1630" data-mp="2ufosrstpybwq">jouwbedrijf</span>.nl
              </h3>
              <p className="text-[#6B7280] text-sm leading-relaxed" data-id="element-1631" data-mp="2udc7p6snzlfg">
                Voor de grote communicatie met klanten, partners en het algemene contact. Dit is het adres dat op je website, visitekaartje en briefpapier staat.
              </p>
            </div>

            {/* Administratie */}
            <div className="bg-white rounded-2xl p-8 shadow-md" data-id="element-1632" data-mp="2bguai9v1qtbt">
              <div className="w-14 h-14 bg-[#007AFF] bg-opacity-10 rounded-full flex items-center justify-center text-[#007AFF] mb-4" data-id="element-1633" data-mp="2bif49x9je88m">
                <FileTextIcon className="h-7 w-7" data-id="element-1634" data-mp="1j0jk886wpj57" />
              </div>
              <p className="text-xs uppercase tracking-wide text-[#6B7280] mb-1" data-id="element-1635" data-mp="1iyp1n0j3y34a">
                Zakelijke accounts en software
              </p>
              <h3 className="font-bold text-[#111827] text-xl mb-3 font-mono" data-id="element-1636" data-mp="3e47wq45z5g3e">
                administratie@<span className="text-[#007AFF]" data-id="element-1637" data-mp="3j2j2a2p8l4ft">jouwbedrijf</span>.nl
              </h3>
              <p className="text-[#6B7280] text-sm leading-relaxed" data-id="element-1638" data-mp="3nthqdoggv6tm">
                Voor je Google-account, software-aanmeldingen en alle andere services waar een zakelijk account makkelijker werkt. Geen verwarring met je dagelijkse mail.
              </p>
            </div>

            {/* Facturen */}
            <div className="bg-white rounded-2xl p-8 shadow-md" data-id="element-1639" data-mp="303na0wwxv04m">
              <div className="w-14 h-14 bg-[#007AFF] bg-opacity-10 rounded-full flex items-center justify-center text-[#007AFF] mb-4" data-id="element-1640" data-mp="30614b3jso8b4">
                <ReceiptIcon className="h-7 w-7" data-id="element-1641" data-mp="394d15nb982c0" />
              </div>
              <p className="text-xs uppercase tracking-wide text-[#6B7280] mb-1" data-id="element-1642" data-mp="392ikil70mwdq">
                Boekhouding en facturatie
              </p>
              <h3 className="font-bold text-[#111827] text-xl mb-3 font-mono" data-id="element-1643" data-mp="odl1nk3tnabu">
                facturen@<span className="text-[#007AFF]" data-id="element-1644" data-mp="yiueimqgk2kl">jouwbedrijf</span>.nl
              </h3>
              <p className="text-[#6B7280] text-sm leading-relaxed" data-id="element-1645" data-mp="tjhro1zl6vi0">
                Voor alle inkomende en uitgaande facturen. Stuur dit adres door naar je boekhouder of administratiekantoor, en jouw eigen inbox blijft schoon.
              </p>
            </div>
          </div>

          <div className="max-w-3xl mx-auto mt-10 bg-white rounded-2xl p-6 text-center shadow-sm" data-id="element-1646" data-mp="kp3eq62j3x22">
            <p className="text-[#6B7280] leading-relaxed" data-id="element-1647" data-mp="fpgq70h92udy">
              Drie mailboxen, allemaal in hetzelfde pakket. Bij <span className="font-semibold text-[#111827]" data-id="element-1648" data-mp="5tngrpytvh0e">{activePackage.shortName}</span> betaal je voor drie mailboxen <span className="font-bold text-[#007AFF]" data-id="element-1649" data-mp="1sbm3613k5lc">{(parseFloat(activePackage.price.replace('€', '').replace(',', '.')) * 3).toFixed(2).replace('.', ',')} euro per maand</span>, jaarlijks vastgelegd.
            </p>
          </div>
        </div>
      </div>

      {/* Email marketing via Hostinger Reach */}
      <div className="w-full bg-white py-20 md:py-24" data-id="element-1650" data-mp="238rl06j7z7lg">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1651" data-mp="2376owch9bglh">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1652" data-mp="287udyslxdnd2">
            <span className="inline-block bg-[#007AFF] bg-opacity-10 text-[#007AFF] text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full mb-4" data-id="element-1653" data-mp="1u94jzluek3sx">
              Email marketing
            </span>
            <h2 className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-1654" data-mp="1kii4drmorda8">
              Nieuwsbrieven en campagnes, schaalt met je bedrijf mee
            </h2>
            <p className="text-[#6B7280] text-lg leading-relaxed" data-id="element-1655" data-mp="1aklzhhkcdekk">
              Naast dagelijks mailen kunnen we ook je nieuwsbrieven, campagnes en geautomatiseerde follow-ups voor je inrichten. Kies hieronder de schaal die bij jouw lijst past.
            </p>
          </div>

          {/* Reach toggle */}
          <div className="max-w-5xl mx-auto mb-8" data-id="element-1656" data-mp="16iqwm8mrit4o">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 p-2 bg-[#F7F8FA] rounded-2xl" data-id="element-1657" data-mp="16ih8kqla2w1m">
              {reachTiers.map(tier => {
              const isActive = tier.id === selectedReach;
              return <button key={tier.id} onClick={() => setSelectedReach(tier.id)} className={`flex flex-col items-center justify-center px-2 py-3 rounded-xl font-semibold text-xs transition-all duration-200 ${isActive ? 'bg-white text-[#007AFF] shadow-md' : 'bg-transparent text-[#6B7280] hover:text-[#111827]'}`} data-id="element-1658" data-mp="i08hgejhw6ca">
                    <span className="text-[10px] uppercase tracking-wide opacity-70 mb-1" data-id="element-1659" data-mp="10wzvo9k6jv9h">
                      Tot
                    </span>
                    <span className="text-sm font-bold" data-id="element-1660" data-mp="1fkur3xpkodq9">
                      {tier.contacts.split(' ')[0]}
                    </span>
                  </button>;
            })}
            </div>
          </div>

          {/* Reach detail */}
          <AnimatePresence mode="wait" data-id="element-1661" data-mp="1okhnu67cwvcz">
            <motion.div key={activeReach.id} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -12
          }} transition={{
            duration: 0.25
          }} className="bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] rounded-2xl p-8 md:p-12 max-w-5xl mx-auto shadow-sm border border-gray-100" data-id="element-1662" data-mp="1jo94shq1gi0j">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start" data-id="element-1663" data-mp="v318bgl8qinj">
                <div className="md:col-span-5" data-id="element-1664" data-mp="zzhxme46f327">
                  <div className="bg-white rounded-2xl p-6 md:p-8 shadow-md" data-id="element-1665" data-mp="102xb5ci7c41i">
                    <div className="inline-flex items-center gap-2 bg-[#007AFF] bg-opacity-10 text-[#007AFF] text-xs font-semibold px-3 py-1 rounded-full mb-4" data-id="element-1666" data-mp="14xt7gouh1dkf">
                      <SendIcon className="h-4 w-4" data-id="element-1667" data-mp="1e0lv8i4g375i" />
                      <span data-id="element-1668" data-mp="1dxzsqwwevhu5">{activeReach.range}</span>
                    </div>
                    <div className="flex items-baseline gap-2 mb-2" data-id="element-1669" data-mp="3brwygy0oglma">
                      <span className="text-5xl font-bold text-[#111827]" data-id="element-1670" data-mp="3fsqkfluk7qcj">
                        {activeReach.price}
                      </span>
                    </div>
                    <p className="text-[#6B7280] text-sm mb-6" data-id="element-1671" data-mp="33hpso5d1ws8f">
                      per maand, jaarlijks vastgelegd
                    </p>
                    <div className="space-y-3 border-t border-gray-200 pt-4 mb-6" data-id="element-1672" data-mp="2yiwgcg9i379u">
                      <div className="flex items-center gap-3" data-id="element-1673" data-mp="2uht0htrgdb8m">
                        <CheckCircleIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-1674" data-mp="3r6sbhgjf8r4i" />
                        <span className="text-sm text-[#6B7280]" data-id="element-1675" data-mp="3m7xt2fpd94kx">
                          {activeReach.contacts}
                        </span>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-1676" data-mp="3i4j4h0nd3fvv">
                        <CheckCircleIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-1677" data-mp="3d67t2lhf4xbj" />
                        <span className="text-sm text-[#6B7280]" data-id="element-1678" data-mp="26rgtqm7y1kjm">
                          {activeReach.emails}
                        </span>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-1679" data-mp="5p7m3vzhs69r">
                        <CheckCircleIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-1680" data-mp="5no5u5du5v7z" />
                        <span className="text-sm text-[#6B7280]" data-id="element-1681" data-mp="9s46ns8lecgc">
                          {activeReach.aiCredits}
                        </span>
                      </div>
                    </div>
                    <a href="/#contact" className="inline-flex items-center justify-center w-full bg-[#007AFF] text-white font-semibold py-3 px-6 rounded-lg shadow-md" data-id="element-1682" data-mp="qeg0omjbhjm">
                      Vraag email marketing aan
                      <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-1683" data-mp="tgmvu9tllpv1" />
                    </a>
                  </div>
                </div>

                <div className="md:col-span-7" data-id="element-1684" data-mp="jo79z1f6kh4v">
                  <h3 className="font-bold text-[#111827] text-2xl mb-3" data-id="element-1685" data-mp="jmw8575apjge">
                    Wat zit erin?
                  </h3>
                  <p className="text-[#6B7280] leading-relaxed mb-6" data-id="element-1686" data-mp="1c51k4pcj6ps5">
                    Alles wat je nodig hebt om campagnes te draaien die meegroeien met je bedrijf. Sjablonen op je merk gezet, automatisering vanuit je website, en rapportage waar je iets aan hebt.
                  </p>
                  <ul className="space-y-3 mb-6" data-id="element-1687" data-mp="2ruj6qhcpodsu">
                    <li className="flex items-start gap-3" data-id="element-1688" data-mp="35mpcram5h4ek">
                      <SendIcon className="h-5 w-5 text-[#007AFF] mt-0.5 flex-shrink-0" data-id="element-1689" data-mp="31m5jii4p6kxp" />
                      <span className="text-[#6B7280] leading-relaxed" data-id="element-1690" data-mp="2dwkvvlxlmzi3">
                        Nieuwsbrieven plannen, met tracking per open en klik
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-1691" data-mp="2mx99ifj2yxug">
                      <SparklesIcon className="h-5 w-5 text-[#007AFF] mt-0.5 flex-shrink-0" data-id="element-1692" data-mp="2myu3o41hgers" />
                      <span className="text-[#6B7280] leading-relaxed" data-id="element-1693" data-mp="2hwmn115pkaj4">
                        AI e-mail templates en onderwerpregel-generator
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-1694" data-mp="3tcjr5wm4b10x">
                      <BotIcon className="h-5 w-5 text-[#007AFF] mt-0.5 flex-shrink-0" data-id="element-1695" data-mp="bv1hoiw2704z" />
                      <span className="text-[#6B7280] leading-relaxed" data-id="element-1696" data-mp="7t6hxits0utb">
                        Welkomst- en opvolg-e-mails over dagen of weken
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-1697" data-mp="3u04tsnkg0cew">
                      <UsersIcon className="h-5 w-5 text-[#007AFF] mt-0.5 flex-shrink-0" data-id="element-1698" data-mp="3u1zd5xg2fwgv" />
                      <span className="text-[#6B7280] leading-relaxed" data-id="element-1699" data-mp="vhzijslcfqu9">
                        Onbeperkte doelgroepsegmenten, handmatig of met AI
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-1700" data-mp="lr4tkggpqd54">
                      <TrendingUpIcon className="h-5 w-5 text-[#007AFF] mt-0.5 flex-shrink-0" data-id="element-1701" data-mp="gu4q8db8zohp" />
                      <span className="text-[#6B7280] leading-relaxed" data-id="element-1702" data-mp="1e9ikdw3msyup">
                        Volledig eigen branding, geen Reach-logo onder je mail
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-1703" data-mp="1gtycdltqcmeb">
                      <CheckCircleIcon className="h-5 w-5 text-[#007AFF] mt-0.5 flex-shrink-0" data-id="element-1704" data-mp="1gvj6jac4u3bn" />
                      <span className="text-[#6B7280] leading-relaxed" data-id="element-1705" data-mp="1lum5cd1irdb6">
                        Sjablonen die we voor jou op merk zetten, plus synchronisatie met je website
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Norden email security */}
      <div className="w-full bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] py-20 md:py-24" data-id="element-1706" data-mp="24pay81bzxx9z">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1707" data-mp="29k6z7qz7nuz8">
          <div className="max-w-4xl mx-auto" data-id="element-1708" data-mp="2egnowpm26hef">
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm" data-id="element-1709" data-mp="2jgjrckf3y0rv">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center" data-id="element-1710" data-mp="2je735pirau6d">
                <div className="text-center md:text-left" data-id="element-1711" data-mp="2ni513wwchyxu">
                  <div className="inline-block" data-id="element-1712" data-mp="occ0titifzkx">
                    <div className="w-48 h-48 bg-gradient-to-br from-[#007AFF] to-[#005FCC] rounded-full flex items-center justify-center p-4" data-id="element-1713" data-mp="ob0xtl7w2vuv">
                      <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden bewaakt zakelijke e-mail hosting" className="w-full h-full object-cover rounded-full" data-id="element-1714" data-mp="x5p0r4pzs1gi" />
                    </div>
                  </div>
                </div>
                <div data-id="element-1715" data-mp="1251lnjxayygg">
                  <h2 className="font-bold text-[#111827] text-3xl mb-4" data-id="element-1716" data-mp="123gtu22dhtma">
                    Hoe we je oplevering aanpakken
                  </h2>
                  <p className="text-[#6B7280] text-lg leading-relaxed mb-6" data-id="element-1717" data-mp="1g1ym8d383uaj">
                    Wij doen de DNS, de SPF, DKIM en DMARC, de domeinverificatie en de oplevering in Outlook of Apple Mail. Jij krijgt een werkende mailbox op de afgesproken dag, met je oude mail meeverhuisd als je dat wilt.
                  </p>
                  <div className="space-y-3" data-id="element-1718" data-mp="33pm709z1p4sz">
                    <div className="flex items-center gap-3" data-id="element-1719" data-mp="33qns779rz7of">
                      <div className="w-6 h-6 bg-[#007AFF] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1720" data-mp="37rzb6ty58unn">
                        <CheckCircleIcon className="h-4 w-4 text-white" data-id="element-1721" data-mp="2tzjh4in806yv" />
                      </div>
                      <span className="text-[#6B7280]" data-id="element-1722" data-mp="2yy4emab38cjm">
                        DNS, SPF, DKIM en DMARC standaard ingericht
                      </span>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-1723" data-mp="2p1hw3s68hxkr">
                      <div className="w-6 h-6 bg-[#007AFF] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1724" data-mp="2b1glhl063bs3">
                        <CheckCircleIcon className="h-4 w-4 text-white" data-id="element-1725" data-mp="2g01o1r2w67jp" />
                      </div>
                      <span className="text-[#6B7280]" data-id="element-1726" data-mp="99kdgld7kk22">
                        Bestaande mail meeverhuisd waar mogelijk
                      </span>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-1727" data-mp="2acbdty945uf3">
                      <div className="w-6 h-6 bg-[#007AFF] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1728" data-mp="2abtbqkzrslz9">
                        <CheckCircleIcon className="h-4 w-4 text-white" data-id="element-1729" data-mp="25b5nu86u8l99" />
                      </div>
                      <span className="text-[#6B7280]" data-id="element-1730" data-mp="20g00r61p90jl">
                        Persoonlijke uitleg over je mailapp na oplevering
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA-rij */}
      <div className="w-full bg-gradient-to-br from-[#007AFF] to-[#005FCC] py-16 md:py-20" data-id="element-1731" data-mp="1hne5onym9e02">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] text-center" data-id="element-1732" data-mp="1coukipruk68r">
          <h2 className="font-bold text-white text-3xl md:text-4xl mb-4" data-id="element-1733" data-mp="18mzj7l9x1suz">
            Combineer je e-mail met de rest
          </h2>
          <p className="text-white text-opacity-90 text-lg leading-relaxed mb-8 max-w-2xl mx-auto" data-id="element-1734" data-mp="13oxqs9qloh6u">
            Email, hosting en website bij één partij betekent korte lijnen, nét als bij de rest van je dienstverlening.
          </p>
          <div className="flex flex-wrap gap-4 justify-center" data-id="element-1735" data-mp="3k8hyqj1zzwdb">
            <a href="/hosting" className="inline-flex items-center px-6 py-3 bg-white text-[#007AFF] font-semibold rounded-lg shadow-md" data-id="element-1736" data-mp="3oczg6bkvxxkx">
              Bekijk hosting (€34,99 per maand)
              <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-1737" data-mp="2wla1moeq3tmd" />
            </a>
            <a href="/websites" className="inline-flex items-center px-6 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-lg" data-id="element-1738" data-mp="30mbu8me4x7fd">
              Website laten maken (vanaf €499)
              <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-1739" data-mp="3ac567oi6vok5" />
            </a>
          </div>
        </div>
      </div>

      <Footer data-id="element-1740" data-mp="2cx0y5i7qc17k" />
    </div>;
};